import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  DEFAULT_NODE_TYPE_FILTERS,
  NodeTypeFilterSettings,
  NetworkData,
  NodeType,
} from '../types/cytoscape';
import { filterGraphByNodeType } from '@/features/connect-dots/utils/networkFilters';
import { useNetworkData } from '@/features/connect-dots/hooks/useNetworkData';
import { useNodeSelection } from '@/features/connect-dots/hooks/useNodeSelection';
import { useNetworkState } from '@/features/connect-dots/hooks/useNetworkState';

import cytoscape from 'cytoscape';

const initialData: NetworkData = { nodes: [], edges: [] };

export const useNetworkElements = () => {
  const { cytoscapeCoreRefObject } = useNetworkState();
  // 元のネットワークデータ（未フィルター）
  const [networkData, setNetworkData] = useState<NetworkData>(initialData);

  // ノードタイプごとの数辞書
  const [nodeTypeCounts, setNodeTypeCounts] = useState<{
    [key in NodeType]?: number;
  }>({});

  // 選択ノードID
  const { selectedNodeIds } = useNodeSelection();

  // 検索ノードID
  const [searchedNodeIds] = useState<Set<string>>(new Set<string>());

  // ノードタイプごとの数をセットする
  useEffect(() => {
    if (!networkData || !networkData.nodes) return;

    const counts: { [key in NodeType]?: number } = {};

    networkData.nodes.forEach((node) => {
      const nodeType = node.nodeType;
      if (nodeType in counts) {
        counts[nodeType]! += 1;
      } else {
        counts[nodeType] = 1;
      }
    });

    setNodeTypeCounts(counts);
  }, [networkData]);

  // フィルター設定の状態
  // ノードタイプ選択条件の追加
  const [nodeTypeFilters, setNodeTypeFilters] =
    useState<NodeTypeFilterSettings>(DEFAULT_NODE_TYPE_FILTERS);

  const prevNetworkRef = useRef<NetworkData | null>(null);

  const { error: networkError, network, isLoading } = useNetworkData();

  // networkDataの更新時のみset
  useEffect(() => {
    if (JSON.stringify(prevNetworkRef.current) !== JSON.stringify(network)) {
      prevNetworkRef.current = network;

      if (network) {
        setNetworkData(network);
      } else {
        setNetworkData(initialData);
      }
    }
  }, [network]);

  // conditionSettings で、networkDataのフィルタリングを行う
  // （※ここでいうフィルタリングは、UI上のフィルタリングではない）
  const displayedData = filterGraphByNodeType(networkData, nodeTypeFilters);
  // filteredDataを明示的に監視して状態を更新
  const [isFilteringCompleted, setIsFilteringCompleted] = useState(false);
  useEffect(() => {
    setIsFilteringCompleted(
      !!displayedData?.nodes && displayedData.nodes.length > 0
    );
  }, [displayedData]);

  // 単一ノードタイプのフィルター更新関数
  const updateNodeTypeFilter = useCallback(
    (nodeType: NodeType, isVisible: boolean) => {
      setNodeTypeFilters((prev) => ({
        ...prev,
        [nodeType]: isVisible,
      }));
    },
    []
  );

  useEffect(() => {
    // nodeTypeFilters に基づき、ノードの表示/非表示を切り替え
    cytoscapeCoreRefObject?.nodes().forEach((node) => {
      const nodeType: NodeType = node.data('nodeType');
      const isVisible = nodeTypeFilters[nodeType];

      // ノードが選択されている場合は表示する
      if (selectedNodeIds && selectedNodeIds.has(node.id())) {
        node.style('display', 'element');
      } else {
        node.style('display', isVisible ? 'element' : 'none');
      }
    });

    // .selected クラスを持つノーマルタグノードは表示する
    if (selectedNodeIds && selectedNodeIds.size > 0) {
      selectedNodeIds.forEach((nodeId) => {
        const node = cytoscapeCoreRefObject?.getElementById(nodeId);
        if (node && node.data('nodeType') === NodeType.NORMAL) {
          node.style('display', 'element');
          node.addClass('selected');
        }
      });
    }
  }, [selectedNodeIds, nodeTypeFilters, cytoscapeCoreRefObject]);

  const elements = useMemo<cytoscape.ElementDefinition[]>(() => {
    if (!network || !network.nodes || !network.edges) {
      return [];
    }

    const nodes = network.nodes.map((node) => ({
      data: {
        ...node,
        highlighted: searchedNodeIds.has(node.id),
        isSelected: selectedNodeIds.size > 0 && selectedNodeIds.has(node.id),
      },
      group: 'nodes' as const,
      classes: [
        node.nodeType || '',
        node.nodeType === NodeType.SOCIAL_ISSUE ||
        node.nodeType === NodeType.CROSS_ISSUE
          ? 'social-issue'
          : '',
        searchedNodeIds.has(node.id) ? 'highlighted' : '',
        selectedNodeIds.size > 0 && selectedNodeIds.has(node.id)
          ? 'selected'
          : '',
      ]
        .filter(Boolean)
        .join(' '),
    }));

    const edges = network.edges.map((edge) => ({
      data: {
        ...edge,
        highlighted:
          searchedNodeIds.has(edge.source) || searchedNodeIds.has(edge.target),
      },
      group: 'edges' as const,
      classes: [
        searchedNodeIds &&
          (searchedNodeIds.has(edge.source) ||
            searchedNodeIds.has(edge.target)) &&
          'highlighted',
      ]
        .filter(Boolean)
        .join(' '),
    }));

    return [...nodes, ...edges];
  }, [network, selectedNodeIds, searchedNodeIds]);

  return {
    // ネットワークデータ関連
    isLoading,
    networkError,

    // cytoscape上での表示切替後のデータ
    displayedData,

    // フィルター設定と更新関数
    nodeTypeFilters,
    setNodeTypeFilters,
    updateNodeTypeFilter,

    // フィルタリング完了状態
    isFilteringCompleted,

    // Cytoscape要素
    elements,

    // 選択ノードID
    selectedNodeIds,

    // 検索ノードID
    searchedNodeIds,

    // ノードタイプごとの数辞書
    nodeTypeCounts,
  };
};
